import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  SUBJECT_VOCABULARY_BY_VERTICAL,
  hasStatusChoice,
  type CreateSubjectBody,
  type NameConflictDetails,
  type SubjectStatus,
} from '@ni/shared';
import { useOrg } from '../api/app-context';
import { useCreateSubject } from '../api/hooks';
import { useAccountForNewRecord } from './accounts';
import { nameConflictOf } from '../api/errors';
import {
  subjectStatusHint,
  subjectStatusLabel,
  UNKNOWN_VERTICAL_NOTE,
  WHAT_YOU_KNOW,
} from '../lib/copy';
import { noteOverBy } from '../lib/prose-limit';
import { NameConflict } from '../components/name-conflict/name-conflict';
import { NoteLength, noteDescribedBy } from '../components/ui/char-count';
import { ErrorLine } from '../components/ui/error-line';
import { WorkingButton } from '../components/ui/working-button';
import { Field } from '../components/ui/field';
import { TopBar } from '../components/ui/top-bar';
import { StatusDot } from '../components/ui/status-word';
import './new-person.css';

/**
 * The demo's three-field form, verbatim (ui-design §4.2). The status control is
 * **the org's vertical's vocabulary**, not the club's: the options and which of
 * them are live come from `SUBJECT_VOCABULARY_BY_VERTICAL[org.vertical]`, so a
 * second vertical changes the words on this form and nothing else. A value in
 * the vocabulary but not live renders greyed and is never submitted (club stage
 * 1: Member and Contact). Create enters a working state for the length of the
 * request — short now that the reading is a leg, but a round trip that writes
 * several rows is still a moment in which the form must not accept edits.
 *
 * A vertical this build does not carry renders **no status options at all** and
 * leaves Create disabled (design note §0.3) — with a line saying why, because a
 * form that cannot be submitted and does not say so is the worse failure.
 * Falling back to the club's words would file someone under a relationship
 * their org does not have.
 *
 * A vertical with **one word** renders no status field either, and this is the
 * opposite case: there is nothing wrong, so there is no line (personal-vertical
 * note §7). The word is still filed — the route's default status is the first
 * live word and always was — it is simply not asked for, because a radio group
 * with one radio in it is a question with one answer. The form is then the two
 * fields the office actually fills in.
 *
 * Creation does not start a run: the seeded record is worth a glance before
 * spending one, and the Research tab opens with a single green button.
 *
 * The one interruption it has (2026-09-01): a name the org already holds a live
 * person under. The server refuses that create with `NAME_CONFLICT` rather than
 * minting a second node, and the decision panel opens **in the form**, below the
 * fields, with the prose still on screen. Whichever way the office answers, the
 * same three fields are what get filed.
 */
/**
 * The form itself, given its three values rather than owning them — which is
 * what makes the in-flight state testable, since it is only ever reached by
 * submitting. The route owns the fields; the form owns which of the vertical's
 * words are live, and therefore whether what has been typed may be filed.
 *
 * `busy` is the create being in flight, and it takes **every editable control**
 * with it: the name, the status choices and the prose. The mutation captured
 * the values when it fired, so an edit landing mid-flight would never be filed:
 * on success the page navigates away, and on a conflict the panel would be
 * asking about a name that no longer matches the field. The Cancel link stays
 * live — a slow create is exactly when leaving is the thing wanted.
 */
export function NewPersonForm({
  vertical,
  name,
  status,
  prose,
  busy,
  deciding,
  conflict,
  error,
  failed,
  account,
  accountMissing = false,
  onName,
  onStatus,
  onProse,
  onSubmit,
  onUse,
  onCreateNew,
}: {
  vertical: string;
  name: string;
  status: SubjectStatus | null;
  prose: string;
  busy: boolean;
  /**
   * **The protection account picker**, in a security org and nowhere else
   * (security fold, 2026-09-22). It sits directly under Name because it is the
   * first thing filed about the record and the only field the server refuses
   * the create without.
   */
  account?: ReactNode;
  /** A security org with no account chosen yet — Create is held until there is one. */
  accountMissing?: boolean;
  /** Which choice on the conflict panel is in flight: an entity id, or `'new'`. */
  deciding: string | null;
  conflict: NameConflictDetails | null;
  error?: unknown;
  failed?: boolean;
  onName: (name: string) => void;
  onStatus: (status: SubjectStatus) => void;
  onProse: (prose: string) => void;
  onSubmit: () => void;
  onUse: (entityId: string) => void;
  onCreateNew: () => void;
}) {
  const vocabulary = SUBJECT_VOCABULARY_BY_VERTICAL[vertical];

  const live = (candidate: SubjectStatus): boolean =>
    (vocabulary?.live as readonly string[] | undefined)?.includes(candidate) ?? false;

  /* **The cap the incident bought** (2026-09-13). This box is the one a
     7,313-character profile was pasted into, and the create blocks on the
     extraction that reads it — so a paste that cannot come back inside the
     transport's ceiling is refused here rather than three timeouts later. The
     box itself is untouched: nothing is truncated and nothing is hidden, the
     office can see the whole of what they pasted and the count says how far
     over it is. */
  const over = noteOverBy(prose);

  const canSubmit =
    name.trim().length > 0 &&
    prose.trim().length > 0 &&
    over === 0 &&
    !accountMissing &&
    status !== null &&
    live(status);

  return (
    <>
      <div className="form new-person-form">
        <Field label="Name" htmlFor="np-name">
          {/* No browser autocomplete: every name filed here is a new one, so a
              dropdown of previously typed values has nothing useful to offer. */}
          <input
            id="np-name"
            type="text"
            autoComplete="off"
            value={name}
            disabled={busy}
            onChange={(event) => onName(event.target.value)}
          />
        </Field>

        {account ?? null}

        {vocabulary && hasStatusChoice(vertical) ? (
          <div className="field">
            <span className="label">Status</span>
            <div className="segment">
              {vocabulary.statuses.map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  className={candidate === status ? 'seg-option on' : 'seg-option'}
                  aria-pressed={candidate === status}
                  disabled={busy || !live(candidate)}
                  onClick={() => {
                    if (live(candidate)) onStatus(candidate);
                  }}
                >
                  <StatusDot vertical={vertical} status={candidate} />
                  {subjectStatusLabel(vertical, candidate)}
                </button>
              ))}
            </div>
            {subjectStatusHint(vertical) ? (
              <div className="field-note">{subjectStatusHint(vertical)}</div>
            ) : null}
          </div>
        ) : vocabulary ? null : (
          /* No control, because there are no words to put on one — and a line
             saying so, because a form with a dead Create and no explanation is
             worse than one that admits what is wrong. The one-word case above
             falls here as `null` instead: nothing is wrong there, the word is
             already chosen, and a note explaining an absence nobody noticed
             would be the app talking about itself. */
          <div className="field">
            <span className="label">Status</span>
            <div className="field-note">{UNKNOWN_VERTICAL_NOTE}</div>
          </div>
        )}

        <div className={over > 0 ? 'field over' : 'field'}>
          <label className="label" htmlFor="np-know">
            {WHAT_YOU_KNOW}
          </label>
          <textarea
            id="np-know"
            rows={4}
            placeholder="How the name reached you, who knows them"
            value={prose}
            disabled={busy}
            aria-invalid={over > 0 || undefined}
            aria-describedby={noteDescribedBy('np-know', prose)}
            onChange={(event) => onProse(event.target.value)}
          />
          <NoteLength id="np-know" value={prose} />
        </div>
      </div>

      {conflict ? (
        <NameConflict
          details={conflict}
          pending={busy ? deciding : null}
          /* The card's two answers ARE the create, so they are held by
             everything the Create button they stand in for is held by. The
             count and its line are still up the page in the box that went over,
             which is what says why; the picker says its own piece the same
             way. */
          held={over > 0 || accountMissing}
          onUse={onUse}
          onCreateNew={onCreateNew}
        />
      ) : failed ? (
        <ErrorLine error={error} />
      ) : null}

      {/* While the panel is up, its own two answers ARE the create — a second
          green Create beside them would be a third, unlabelled choice. */}
      <div className="form-actions">
        {conflict ? null : (
          <WorkingButton
            label="Create"
            workingLabel="Creating…"
            working={busy}
            disabled={!canSubmit}
            onClick={onSubmit}
          />
        )}
        <Link className="btn act" to="/">
          Cancel
        </Link>
      </div>
    </>
  );
}

/**
 * **What holds the write**, as a function for the reason `organisationBody` is
 * one on the sibling door: the suite has no press, so what the create refuses is
 * asserted here rather than through a button.
 *
 * It is the same three things `canSubmit` withholds Create for, said again at
 * the write — and the third of them is not belt and braces. While the namesake
 * card is up **Create is not on the page at all**, and the card's own two
 * answers call this instead: a form refused on a short note, then pasted full of
 * prose with the card still standing, would otherwise have filed the long note
 * through "Use this record". The card is held as well (`held`), so the office
 * sees the refusal rather than a button that does nothing; this is the half that
 * makes it true rather than merely visible.
 */
export function writeHeld(fields: {
  status: SubjectStatus | null;
  prose: string;
  writing: boolean;
  /** A security org with no protection account chosen; false everywhere else. */
  accountMissing?: boolean;
}): boolean {
  return (
    fields.status === null ||
    noteOverBy(fields.prose) > 0 ||
    fields.writing ||
    fields.accountMissing === true
  );
}

export function NewPerson() {
  const navigate = useNavigate();
  const create = useCreateSubject();
  const org = useOrg();
  const [params] = useSearchParams();

  /* **The account this record is filed under** (security fold, 2026-09-22).
     `?account_id=` preselects — the account page's two Add links carry it — and
     a firm running one engagement is not asked at all, because a menu with one
     item is a question with one answer. A firm running several is asked, and
     the create is held until it is answered: there is no sensible default, and
     "whichever was created first" would file a principal under somebody else's
     engagement. */
  const account = useAccountForNewRecord(params.get('account_id'));

  const [name, setName] = useState('');
  /* The first live value of the org's vertical, not a hardcoded `'prospect'`.
     Null only when this build does not carry the org's vertical, which is the
     case the control renders nothing for. */
  const [status, setStatus] = useState<SubjectStatus | null>(
    SUBJECT_VOCABULARY_BY_VERTICAL[org.vertical]?.live[0] ?? null,
  );
  const [prose, setProse] = useState('');
  /** Which choice on the conflict panel is in flight: an entity id, or `'new'`. */
  const [deciding, setDeciding] = useState<string | null>(null);

  const file = (override: Partial<CreateSubjectBody>) => {
    /* Refused before it is written — and the card's choice is dropped with it,
       since `onSettled` is what normally clears it and no write is about to
       settle. Leaving it set would freeze the card in a working state over a
       create that never fired.

       The null status is named twice: `writeHeld` holds it, and the compiler
       cannot see that through a helper taking an object, so the same question is
       asked inline to narrow the value the mutation is about to send. One rule,
       spelled where the type checker can read it. */
    if (
      status === null ||
      writeHeld({ status, prose, writing: create.isPending, accountMissing: account.missing })
    ) {
      setDeciding(null);
      return;
    }
    create.mutate(
      {
        name: name.trim(),
        status,
        prose: prose.trim(),
        ...(account.accountId ? { account_id: account.accountId } : {}),
        ...override,
      },
      {
        onSuccess: (record) => {
          navigate(`/people/${record.subject.id}`);
        },
        onSettled: () => setDeciding(null),
      },
    );
  };

  /* Editing a field takes the panel back down: the question was asked about a
     name, and a changed name is a different question. `reset()` is what clears
     it, because the panel is rendered from the mutation's own error. */
  const edited = () => {
    if (create.isError) create.reset();
  };

  return (
    <div className="column">
      <TopBar parent={{ to: '/', label: 'People' }} here="New person" />

      <div className="head">
        <div className="titles">
          <div className="title">New person</div>
        </div>
      </div>

      <NewPersonForm
        vertical={org.vertical}
        name={name}
        status={status}
        prose={prose}
        busy={create.isPending}
        account={account.control}
        accountMissing={account.missing}
        deciding={deciding}
        conflict={nameConflictOf(create.error)}
        error={create.error}
        failed={create.isError}
        onName={(next) => {
          setName(next);
          edited();
        }}
        onStatus={setStatus}
        onProse={setProse}
        onSubmit={() => file({})}
        onUse={(entityId) => {
          setDeciding(entityId);
          file({ entity_id: entityId });
        }}
        onCreateNew={() => {
          setDeciding('new');
          file({ force_new: true });
        }}
      />
    </div>
  );
}
